import React, { useRef, useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

interface SwipeGesturesProps {
  children: React.ReactNode;
  onSwipeLeft?: () => void;
  onSwipeRight?: () => void;
  onSwipeUp?: () => void;
  onSwipeDown?: () => void;
  threshold?: number;
  className?: string;
}

interface TouchPoint {
  x: number;
  y: number;
  time: number;
}

// Rutas de navegación para swipe
const NAVIGATION_ROUTES = [
  '/admin',
  '/sales', 
  '/inventory/products',
  '/customers',
  '/reports'
];

export function SwipeGestures({ 
  children, 
  onSwipeLeft,
  onSwipeRight,
  onSwipeUp,
  onSwipeDown,
  threshold = 50,
  className = ""
}: SwipeGesturesProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const [touchStart, setTouchStart] = useState<TouchPoint | null>(null);
  const [touchEnd, setTouchEnd] = useState<TouchPoint | null>(null);

  // Navegación automática entre rutas principales
  const handleAutoNavigation = (direction: 'left' | 'right') => {
    const currentIndex = NAVIGATION_ROUTES.indexOf(location.pathname);
    if (currentIndex === -1) return;

    let nextIndex: number;
    if (direction === 'left') {
      nextIndex = currentIndex + 1;
      if (nextIndex >= NAVIGATION_ROUTES.length) nextIndex = 0;
    } else {
      nextIndex = currentIndex - 1;
      if (nextIndex < 0) nextIndex = NAVIGATION_ROUTES.length - 1;
    }

    navigate(NAVIGATION_ROUTES[nextIndex]);
  };

  const onTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null);
    setTouchStart({
      x: e.targetTouches[0].clientX,
      y: e.targetTouches[0].clientY,
      time: Date.now()
    });
  };

  const onTouchMove = (e: React.TouchEvent) => {
    setTouchEnd({
      x: e.targetTouches[0].clientX,
      y: e.targetTouches[0].clientY,
      time: Date.now()
    });
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;

    const distanceX = touchStart.x - touchEnd.x;
    const distanceY = touchStart.y - touchEnd.y;
    const timeElapsed = touchEnd.time - touchStart.time;
    
    const isLeftSwipe = distanceX > threshold;
    const isRightSwipe = distanceX < -threshold;
    const isUpSwipe = distanceY > threshold;
    const isDownSwipe = distanceY < -threshold;
    
    // Velocidad mínima para considerar un swipe válido
    const velocity = Math.abs(distanceX) / timeElapsed;
    const isValidSwipe = velocity > 0.1 && timeElapsed < 500;

    if (!isValidSwipe) return;

    // Determinar si es swipe horizontal o vertical
    const isHorizontalSwipe = Math.abs(distanceX) > Math.abs(distanceY);

    if (isHorizontalSwipe) {
      if (isLeftSwipe) {
        onSwipeLeft ? onSwipeLeft() : handleAutoNavigation('left');
      } else if (isRightSwipe) {
        onSwipeRight ? onSwipeRight() : handleAutoNavigation('right');
      }
    } else {
      if (isUpSwipe && onSwipeUp) {
        onSwipeUp();
      } else if (isDownSwipe && onSwipeDown) {
        onSwipeDown();
      }
    }
  };

  // Swipe-to-go-back para iOS
  useEffect(() => {
    const handlePopState = () => {
      // Añadir vibración táctil en iOS
      if ('vibrate' in navigator) {
        navigator.vibrate(50);
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  return (
    <div
      ref={containerRef}
      className={`swipe-back ${className}`}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
      style={{ 
        touchAction: 'pan-x pan-y',
        WebkitUserSelect: 'none',
        userSelect: 'none'
      }}
    >
      {children}
    </div>
  );
}

// Hook para usar swipe gestures fácilmente
export function useSwipeGestures(callbacks: {
  onSwipeLeft?: () => void;
  onSwipeRight?: () => void;
  onSwipeUp?: () => void;
  onSwipeDown?: () => void;
}) {
  const navigate = useNavigate();
  const location = useLocation();

  const defaultCallbacks = {
    onSwipeLeft: callbacks.onSwipeLeft || (() => {
      const currentIndex = NAVIGATION_ROUTES.indexOf(location.pathname);
      if (currentIndex !== -1) {
        const nextIndex = (currentIndex + 1) % NAVIGATION_ROUTES.length;
        navigate(NAVIGATION_ROUTES[nextIndex]);
      }
    }),
    onSwipeRight: callbacks.onSwipeRight || (() => {
      const currentIndex = NAVIGATION_ROUTES.indexOf(location.pathname);
      if (currentIndex !== -1) {
        const prevIndex = currentIndex === 0 ? NAVIGATION_ROUTES.length - 1 : currentIndex - 1;
        navigate(NAVIGATION_ROUTES[prevIndex]);
      }
    }),
    onSwipeUp: callbacks.onSwipeUp,
    onSwipeDown: callbacks.onSwipeDown
  };

  return defaultCallbacks;
}